import { MEMBER_ACCESS_REQUIRED_COPY } from '@/lib/auth/capability-copy'
import {
  APP_ERROR_KIND,
  type AppErrorCode,
  type AppErrorData,
  getConvexAppError,
} from '@/lib/errors/app-error-payload'
import { localizeErrorMessage } from '@/lib/errors/error-message-localizer'

export {
  APP_ERROR_CODES,
  APP_ERROR_KIND,
  type AppErrorCode,
  type AppErrorData,
  getConvexAppError,
} from '@/lib/errors/app-error-payload'

type AppErrorOptions = Pick<AppErrorData, 'field' | 'retryable'>

export function createAppErrorData(
  code: AppErrorCode,
  message: string,
  options: AppErrorOptions = {}
): AppErrorData {
  return {
    kind: APP_ERROR_KIND,
    code,
    message,
    ...(options.field ? { field: options.field } : {}),
    ...(options.retryable !== undefined
      ? { retryable: options.retryable }
      : {}),
  }
}

/**
 * Narrow check: is `error` a `ConvexError` carrying a typed app-error
 * payload with the given `code`?
 *
 * Use this at API-route catch sites that need to map a typed throw to
 * a specific HTTP status code (e.g. `VALIDATION_ERROR` → 400). Reading
 * `error.message` directly is unreliable because `ConvexError`
 * stringifies its data — `new ConvexError({ message: 'X' }).message`
 * is a JSON object string, NOT `'X'`.
 */
export function isAppErrorWithCode(
  error: unknown,
  code: AppErrorCode
): boolean {
  const data = getConvexAppError(error)
  return data !== null && data.code === code
}

/**
 * The message to SHOW for a typed app error, else `fallbackMessage`. In the
 * browser of a reader of another language it is translated
 * (`localizeErrorMessage`), so code that branches on the failure compares
 * `getConvexAppError(error)?.message` (always English) or the `code`.
 */
export function getConvexErrorMessage(error: unknown): string | undefined
export function getConvexErrorMessage(
  error: unknown,
  fallbackMessage: string
): string
export function getConvexErrorMessage(
  error: unknown,
  fallbackMessage?: string
): string | undefined {
  const appError = getConvexAppError(error)
  if (appError) {
    return localizeErrorMessage(viewerRefusalCopy(appError) ?? appError.message)
  }

  return fallbackMessage
}

/**
 * A brand-role refusal from a Convex guard (`convex/shared/brand_auth.ts`)
 * surfaces as the SAME sentence the gated controls show, so a control the UI
 * did not gate still explains itself to a viewer.
 */
function viewerRefusalCopy(appError: {
  code: string
  message: string
}): string | null {
  return appError.code === 'NOT_AUTHORIZED' &&
    appError.message === BRAND_MEMBER_REFUSAL_MESSAGE
    ? MEMBER_ACCESS_REQUIRED_COPY
    : null
}

/** The exact message `requireBrandMember*` throws (pinned by tests). */
const BRAND_MEMBER_REFUSAL_MESSAGE = 'Brand member access required'

/**
 * A Convex ARGUMENT-validation failure — a wrapper sent the deployed validator
 * a field it doesn't declare (or dropped a required one). It arrives as a plain
 * `Error` whose message embeds `ArgumentValidationError`, and it is ALWAYS a
 * server bug, never bad user input. Surface it distinctly so it can never hide
 * behind a generic 500 (a forwarded `actor`/`surface`/`requireFlip` bag broke
 * scheduled-send cancel this way). String-sniff, mirroring the existing
 * `isConvexCursorParseError` precedent in `lib/data/convex/shared.ts`.
 *
 * DEV-ONLY signal: a PRODUCTION Convex deployment REDACTS non-ConvexError
 * messages to `[Request ID: …] Server Error` (the incident's own prod log
 * shows exactly this), so this detector cannot fire against prod. The real
 * prod protections are upstream: the `NoExcess` compile-time guard on the
 * wrappers and the wire-payload tests (`tests/sends-convex-arg-exactness`),
 * which stop the drift from ever deploying.
 */
export function isConvexArgumentValidationError(error: unknown): boolean {
  return (
    error instanceof Error && error.message.includes('ArgumentValidationError')
  )
}
